import InvoiceHomeDetailCard from "./InvoiceHomeDetailCard";
import { getInvoiceDetailsCardInfo } from "../invoice-master/invoice-helper";
import { RootState } from "src/context/store";
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { invoiceActions } from "src/context/invoice-slice";
import { fetchInvoicesData } from "src/services/invoice-api";
import { useToast } from "src/hooks/useToast";
import { Spinner } from "src/components";
import useInvoiceSearchGate, { INVOICE_SEARCH_PROMPT } from "src/hooks/useInvoiceSearchGate";

const AllInvoices = ({ searchValue, searchCategory, selectedCount }: { searchValue?: string; searchCategory?: string; selectedCount?: string }) => {
  const { invoiceData, invoiceAnalysisData, selectedInvoices } = useSelector((state: RootState) => state.invoice);
  const [hasMore, setHasMore] = useState(true);
  const { InvoiceFilters } = useSelector((state: RootState) => state.invoice);
  const [page, setPage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { toast } = useToast();
  const latestRequestId = useRef(0);
  const { canFetch, awaitingCriteria } = useInvoiceSearchGate(InvoiceFilters, searchValue); // M10-16144
  //M10-9862
  // const resetFilter = () => {
  //   const hasActiveFilters = Object.values(InvoiceFilters).some((value: any) => value?.length > 0);

  //   if (hasActiveFilters) {
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "company", value: [] }));
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "division", value: [] }));
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "stockist", value: [] }));
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "status", value: [] }));
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "assignee", value: [] }));
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "city", value: [] }));
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "district", value: [] }));
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "state", value: [] }));
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "fuidNo", value: [] }));
  //     dispatch(invoiceActions.setInvoiceFilters({ key: "sorting", value: [] }));
  //     dispatch(invoiceActions.setIsResetFilter(false));
  //   }
  // };

  const searchParams = useMemo(
    () => ({
      company_code: InvoiceFilters?.company?.map((item) => item.code)?.join(",") || "",
      division_code: InvoiceFilters?.division?.map((item) => item.company_divisioncode)?.join(",") || "",
      startDate: InvoiceFilters?.dateRange?.from,
      endDate: InvoiceFilters?.dateRange?.to,
      assignee_id: InvoiceFilters?.assignee?.map((item) => item.invoice_assignment_id)?.join(",") || "",
      city: InvoiceFilters?.city?.map((item) => item.city_name?.split("|")?.[0]?.trim())?.join(",") || "",
      district: InvoiceFilters?.district?.map((item) => item.district_name)?.join(",") || "",
      state: InvoiceFilters?.state?.map((item) => item.state_name)?.join(",") || "",
      ordering: InvoiceFilters?.sorting?.[0]?.key,
      textract_configured: InvoiceFilters?.textract?.[0]?.key, //textract filter
      fuid_status_id: InvoiceFilters?.status?.length || 0 > 0 ? InvoiceFilters?.status?.map(item => item.fuid_status_id).join(",") || 1 : (searchValue && searchValue.length > 0 ? "" : 1),
      page_size: 10,
      page_number: 1,
      screen: "Entry",
      dateType: InvoiceFilters?.dateType?.[0]?.key || "",
      uploadType: InvoiceFilters?.uploadType?.[0]?.key,
      moduleType: InvoiceFilters?.moduleType?.[0]?.key,
      //M10-14101
      order_mode: InvoiceFilters?.orderMode?.[0]?.key,
      cndn_remarks_code_int: InvoiceFilters?.orderType?.[0]?.key,
      //M10-14101
      rejection_code: InvoiceFilters?.rejection?.map((item: any) => item.rejection_code)?.join(",") || "",
      ...(searchValue && searchCategory ? { [searchCategory]: searchValue } : {}),
      ...(searchValue ? {} : { is_escalated: false })
    }),
    [searchValue, searchCategory, InvoiceFilters]
  );

  const fetchTabData = useCallback(
    async (count?: any) => {
      const requestId = ++latestRequestId.current;
      setIsLoading(true);
      dispatch(invoiceActions.setInvoiceDetails([]));
      dispatch(invoiceActions.setInvoicesTextractData([]));
      dispatch(invoiceActions?.setInvoiceMappingOrderData([]));
      dispatch(invoiceActions?.setInvoiceMappingOrderedProducts([]));
      dispatch(invoiceActions?.setInvoiceMappingOriginalDescriptions([]));
      window.localStorage.removeItem("counterCompanyLocationId");
      window.localStorage.removeItem("counterOrderId");
      if (count) {
        searchParams.page_size = count
        searchParams.page_number = 1
      }
      try {
        const allInvoicesRes = await fetchInvoicesData(searchParams);
        if (requestId !== latestRequestId.current) return;
        dispatch(invoiceActions.setInvoiceData(allInvoicesRes?.data?.data?.invoiceData || []));
        if (count) {
          dispatchInvoicesSelection(allInvoicesRes?.data?.data?.invoiceData, count);
        }
        if (allInvoicesRes?.data?.data) {
          dispatch(invoiceActions.setInvoiceAnalysisData(
            {
              entry: {
                "All Invoices": allInvoicesRes?.data?.data?.total || 0,
                "Priority Tasks": allInvoicesRes?.data?.data?.count?.count_prioritised || 0, //M10-9511
                "Escalated": allInvoicesRes?.data?.data?.count?.count_escalated || 0,  //M10-9511
                "Rejection Review": allInvoicesRes?.data?.data?.count?.count_rejection_pending ,
                "Feedback from Ext": allInvoicesRes?.data?.data?.count?.count_kam_feedback_pending || 0,
              },
              approval: {
                "All Invoices": invoiceAnalysisData?.approval?.["All Invoices"] || 0,
                "Priority Tasks": invoiceAnalysisData?.approval?.["Priority Tasks"] || 0,
                "Escalated": invoiceAnalysisData?.approval?.["Escalated"] || 0,
              }
            }
          ));
        }
        setPage(2);
        setHasMore(true);
        toast({ description: "Invoice data fetched successfully", variant: "default" });
      } catch (error) {
        if (requestId !== latestRequestId.current) return;
        console.error("Error fetching invoices data:", error);
        toast({ description: "Failed to fetch invoice data", variant: "destructive" });
      } finally {
        if (requestId === latestRequestId.current) setIsLoading(false);
      }
    },
    [dispatch, searchParams]
  );

  const fetchMoreData = async () => {
    if (isLoading) return;
    try {
      const nextPageParams = { ...searchParams, page_number: page, skip_counts: true }; // M10-16144: page N reads only invoiceData
      const res = await fetchInvoicesData(nextPageParams);
      const newData = res?.data?.data?.invoiceData;
      if (!newData || newData.length === 0) {
        setHasMore(false);
        return;
      }
      dispatch(invoiceActions.setInvoiceData([...(invoiceData || []), ...newData]));
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching more invoices", error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(invoiceActions.setInvoiceEntryTab(0));
    // M10-16144: roles outside the default-load list wait for a search/filter before fetching
    if (!canFetch) {
      latestRequestId.current++; // drop any response still in flight from before the filters were cleared
      dispatch(invoiceActions.setInvoiceData([]));
      setHasMore(false);
      setIsLoading(false);
      return;
    }
    fetchTabData();
  }, [fetchTabData, canFetch]);

  useEffect(() => {
    setPage(1);
    dispatch(invoiceActions.setInvoicesTextractData([]));//M10-9862
    if (selectedCount && !isNaN(Number(selectedCount))) {
      handleSelectCount()
    } else {
      dispatch(invoiceActions.clearSelectedInvoices())
    }
    // Note: do NOT call fetchTabData() here for searchValue changes — the
    // [fetchTabData] effect above already re-fires when searchValue changes
    // (searchValue → searchParams memo → fetchTabData identity).
  }, [searchValue, selectedCount]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        {" "}
        <Spinner />
      </div>
    );
  }

  // Dispatch function to select invoices
  const dispatchInvoicesSelection = (invoiceDetails?: any, count?: number) => {
    // Limit the array to the 'count' number of items (if count is less than array length)
    const limitedInvoiceData = invoiceDetails.slice(0, count);
    // Now run forEach on the limited array
    limitedInvoiceData.forEach((invoiceData: any) => {
      const invoiceKey = invoiceData?.stockist_invoice_attachment_id;
      if (invoiceKey) {
        dispatch(
          invoiceActions.toggleSelectedInvoice({
            key: invoiceKey,
            fuid_no: invoiceData?.fuid_no,
            invoice_assignment_id: invoiceData?.InvoiceAssignment?.invoice_assignment_id,
          })
        );
      }
    });
  };

  // Function to handle selecting based on count
  const handleSelectCount = async () => {
    // Use the selectCount value here
    const count = Number(selectedCount) || 0;

    // Check if the count exceeds the length of invoice data
    // M10-16144: no list fetch until the user has searched/filtered (see useInvoiceSearchGate)
    if (count > invoiceData.length && canFetch) {
      await fetchTabData(count)
      console.log("Count exceeds available invoices", count, invoiceData.length);
      return; // Optionally handle this case with an alert or notification
    } else {
      console.log("Selecting invoices...", count, invoiceData.length);
      dispatchInvoicesSelection(invoiceData, count); //M10-11655
      return
    }
  };


  return (
    <div className="mt-2 w-full pb-4">
      {selectedInvoices?.length > 0 && (
        <div className="mt-4 mb-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-between">
          <span className="text-sm font-medium">{selectedInvoices?.length} invoice(s) selected</span>
        </div>
      )}
      {invoiceData?.length === 0 && (
        <div className="flex justify-center items-center h-[20vh]">
          <p className="text-lg text-gray-500">{awaitingCriteria ? INVOICE_SEARCH_PROMPT : "No invoices found"}</p>
        </div>
      )}

      <div
        id="scrollableDiv"
        className="h-[calc(100vh-250px)] relative overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        <InfiniteScroll
          dataLength={invoiceData?.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={""}
          className="h-[60vh]"
          scrollableTarget="scrollableDiv"
          endMessage={awaitingCriteria ? null : <div className="py-4 text-center text-gray-500">No more invoices to load</div>}
          style={{ overflow: "hidden" }} // Prevents extra spacing when empty
        >
          {/* <ul className="flex flex-col gap-5 p-4 h-[100vh]"> */}
          {invoiceData?.map((data: any, index: number) => (
            <li className="rounded-xl border shadow-sm border-gray-200 list-none my-4" key={index}>
              <InvoiceHomeDetailCard
                status={data?.fileStatusHdr?.fuid_status_desc}
                data={getInvoiceDetailsCardInfo(data, false)} //M10-15065
                checkDataType={false}
                invoiceData={data}
                viewDetails={true}
                index={index}
                fetchTabData={fetchTabData}
              />
            </li>
          ))}
          {/* </ul> */}
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default AllInvoices;
